import moment from 'moment'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { ScheduledReportDTO, ScheduledReports, periodToInterval } from 'services/user'
import { ScheduledReportRow } from './types'

// Live data for the Scheduled Reports page (DEV-2377, §6.8), mapped to the row
// shape the table renders.

const FREQUENCY: Record<string, ScheduledReportRow['frequency']> = {
	DAILY: 'Daily',
	WEEKLY: 'Weekly',
	MONTHLY: 'Monthly',
	QUARTERLY: 'Quarterly'
}

const PERIOD: Record<string, string> = {
	LAST_7: 'Last 7 days',
	LAST_30: 'Last 30 days',
	THIS_MONTH: 'This month',
	LAST_MONTH: 'Last month',
	THIS_QUARTER: 'This quarter',
	LAST_QUARTER: 'Last quarter'
}

const fmtDate = (d?: Date | string | null): string | null =>
	d ? moment(d).format('MMM D, YYYY') : null

export const toReportRow = (dto: ScheduledReportDTO): ScheduledReportRow => ({
	id: dto.id,
	viewId: dto.view_id,
	name: dto.label ?? 'Report',
	nextGeneration: fmtDate(dto.scheduled_for) ?? '—',
	lastSent: fmtDate(dto.generated_at),
	frequency: FREQUENCY[periodToInterval(dto.period)] ?? 'Monthly',
	period: PERIOD[dto.analytics_date_range] ?? dto.analytics_date_range,
	recipients: dto.recipients,
	scheduledBy: dto.created_by_user_id,
	// Name is resolved server-side (the account's client-side member list isn't
	// always name-enriched); fall back to the id only if the backend couldn't.
	scheduledByName: dto.created_by_name || dto.created_by_user_id,
	createdAt: fmtDate(dto.created_at) ?? '—',
	// Absent on non-list payloads — treat unknown as accessible so nothing is
	// wrongly locked; the list endpoint always sets it.
	viewAccessible: dto.view_accessible ?? true,
	generated: !!dto.last_generation_url
})

export const useScheduledReports = () => {
	const { accountId, assetId } = useParams<{ accountId: string; assetId: string }>()
	const enabled = !!accountId && !!assetId
	const query = useQuery(
		['scheduled-reports', accountId, assetId],
		() => ScheduledReports.list(accountId as string, assetId as string),
		{ enabled }
	)
	const raw = query.data ?? []
	return {
		rows: raw.map(toReportRow),
		raw,
		isLoading: query.isLoading,
		// Surface fetch failure so the page can distinguish "no reports" from "the
		// request failed" instead of both collapsing to an empty list.
		isError: query.isError,
		refetch: query.refetch
	}
}
